import evaluatePasswordPolicy, {PASSWORD_POLICY_I18N_KEYS} from 'js/lib/password-policy'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  data() {
    return {
      loginValidationErrors: {
        email: undefined,
        password: undefined
      },
      registerValidationErrors: {
        email: undefined,
        password: undefined,
        password_confirmation: undefined
      },
      loginValidationTouched: {
        email: false,
        password: false
      },
      registerValidationTouched: {
        email: false,
        password: false,
        password_confirmation: false
      },
      hasLoginBackendValidationErrors: false,
      hasRegistrationBackendValidationErrors: false
    }
  },
  computed: {
    isLoginFrontendValid() {
      return this.isValidEmail(this.loginEmail) &&
        this.isCompleted(this.loginPassword)
    },
    isRegistrationFrontendValid() {
      return this.isValidEmail(this.registerEmail) &&
        this.isCompleted(this.registerPassword) &&
        this.passwordPolicyViolations(this.registerPassword, this.registerEmail).length === 0 &&
        this.isCompleted(this.registerPasswordConfirmation) &&
        this.registerPasswordConfirmation === this.registerPassword &&
        this.registerAcceptConditions === true
    },
    isLoginSubmitEnabled() {
      return this.isLoginFrontendValid && !this.hasLoginBackendValidationErrors
    },
    isRegistrationSubmitEnabled() {
      return this.isRegistrationFrontendValid &&
        !this.hasRegistrationBackendValidationErrors
    },
    loginEmailFieldError() {
      return this.loginValidationErrors.email
    },
    loginPasswordFieldError() {
      return this.loginError || this.loginValidationErrors.password
    },
    registerEmailFieldError() {
      return this.registerErrors.email || this.registerValidationErrors.email
    },
    registerPasswordFieldError() {
      return this.registerErrors.password || this.registerValidationErrors.password
    },
    registerPasswordConfirmationFieldError() {
      return this.registerErrors.password_confirmation ||
        this.registerValidationErrors.password_confirmation
    }
  },
  watch: {
    loginEmail() {
      this.handleLoginValidationInput('email')
    },
    loginPassword() {
      this.handleLoginValidationInput('password')
    },
    registerEmail() {
      this.handleRegistrationValidationInput('email')
    },
    registerPassword() {
      this.handleRegistrationValidationInput('password')
    },
    registerPasswordConfirmation() {
      this.handleRegistrationValidationInput('password_confirmation')
    },
    registerAcceptConditions() {
      this.handleRegistrationValidationInput('accept')
    }
  },
  methods: {
    isCompleted(value) {
      return typeof value === 'string' && value.trim() !== ''
    },

    isValidEmail(email) {
      return this.isCompleted(email) && EMAIL_PATTERN.test(email.trim())
    },

    passwordPolicyViolations(password, email) {
      return evaluatePasswordPolicy({
        policy: this.getPasswordPolicy,
        password: typeof password === 'string' ? password.trim() : '',
        email: typeof email === 'string' ? email.trim() : ''
      }).violations
    },

    passwordPolicyError(violation) {
      const key = PASSWORD_POLICY_I18N_KEYS[violation]
      const count = this.getPasswordPolicy?.[violation]

      return typeof count === 'number' ? this.$tc(key, count, {count}) : this.$t(key)
    },

    touchLoginValidationField(field) {
      this.loginValidationTouched[field] = true
      this.validateLoginField(field)
    },

    touchRegistrationValidationField(field) {
      if (field !== 'accept') this.registerValidationTouched[field] = true
      this.validateRegistrationField(field)
    },

    touchAllLoginValidationFields() {
      Object.keys(this.loginValidationTouched).forEach(field => {
        this.loginValidationTouched[field] = true
      })
    },

    touchAllRegistrationValidationFields() {
      Object.keys(this.registerValidationTouched).forEach(field => {
        this.registerValidationTouched[field] = true
      })
    },

    validateLoginField(field) {
      if (field === 'email') {
        if (!this.isCompleted(this.loginEmail)) {
          this.loginValidationErrors.email = this.$t('loginForm.validation.emailRequired')

          return false
        }

        if (!this.isValidEmail(this.loginEmail)) {
          this.loginValidationErrors.email = this.$t('loginForm.validation.emailInvalid')

          return false
        }

        this.loginValidationErrors.email = undefined

        return true
      }

      if (field === 'password') {
        if (!this.isCompleted(this.loginPassword)) {
          this.loginValidationErrors.password = this.$t('loginForm.validation.passwordRequired')

          return false
        }

        this.loginValidationErrors.password = undefined

        return true
      }

      return false
    },

    validateRegistrationField(field) {
      if (field === 'email') {
        if (!this.isCompleted(this.registerEmail)) {
          this.registerValidationErrors.email = this.$t('loginForm.validation.emailRequired')

          return false
        }

        if (!this.isValidEmail(this.registerEmail)) {
          this.registerValidationErrors.email = this.$t('loginForm.validation.emailInvalid')

          return false
        }

        this.registerValidationErrors.email = undefined

        return true
      }

      if (field === 'password') {
        if (!this.isCompleted(this.registerPassword)) {
          this.registerValidationErrors.password = this.$t('loginForm.validation.passwordRequired')

          return false
        }

        const violation = this.passwordPolicyViolations(
          this.registerPassword,
          this.registerEmail
        )[0]
        if (violation) {
          this.registerValidationErrors.password = this.passwordPolicyError(violation)

          return false
        }

        this.registerValidationErrors.password = undefined

        return true
      }

      if (field === 'password_confirmation') {
        if (!this.isCompleted(this.registerPasswordConfirmation)) {
          this.registerValidationErrors.password_confirmation =
            this.$t('loginForm.validation.passwordConfirmationRequired')

          return false
        }

        if (this.registerPasswordConfirmation !== this.registerPassword) {
          this.registerValidationErrors.password_confirmation =
            this.$t('loginForm.validation.passwordMismatch')

          return false
        }

        this.registerValidationErrors.password_confirmation = undefined

        return true
      }

      if (field === 'accept') {
        if (!this.registerAcceptConditions) {
          this.registerAcceptConditionsError =
            this.$t('loginForm.register.conditionsError')

          return false
        }

        this.registerAcceptConditionsError = undefined

        return true
      }

      return false
    },

    validateLoginForm() {
      return ['email', 'password']
        .map(field => this.validateLoginField(field))
        .every(Boolean)
    },

    validateRegistrationForm() {
      return ['email', 'password', 'password_confirmation', 'accept']
        .map(field => this.validateRegistrationField(field))
        .every(Boolean)
    },

    handleLoginValidationInput(field) {
      if (this.loginValidationTouched[field]) this.validateLoginField(field)
      if (!this.hasLoginBackendValidationErrors) return

      if (this.validateLoginForm()) this.clearLoginBackendValidationErrors()
    },

    handleRegistrationValidationInput(field) {
      if (field !== 'accept' && this.registerValidationTouched[field]) {
        this.validateRegistrationField(field)
      }
      if (!this.hasRegistrationBackendValidationErrors) return

      if (this.validateRegistrationForm()) {
        this.clearRegistrationBackendValidationErrors()
      }
    },

    clearLoginBackendValidationErrors() {
      this.loginError = undefined
      this.hasLoginBackendValidationErrors = false
    },

    clearRegistrationBackendValidationErrors() {
      this.registerErrors = {
        email: undefined,
        password: undefined,
        password_confirmation: undefined
      }
      this.registerAcceptConditionsError = undefined
      this.registrationError = undefined
      this.keyCloakError = ''
      this.hasRegistrationBackendValidationErrors = false
    }
  }
}
